// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import { Component, OnInit, Inject, ViewChild, ElementRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {
  IAddOrganizationData,
  MaxDrivePerInstallation,
  PlanChoices,
  PlanChoiceValue
} from '../add-organization/add-organization.component';
import { selectableTypes } from '../../../types/subscription';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

@Component({
    selector: 'app-add-child-subscription',
    templateUrl: './add-child-subscription.component.html',
    styleUrls: ['./add-child-subscription.component.scss'],
    standalone: false
})
export class AddChildSubscriptionComponent implements OnInit {
  @ViewChild('durationDelay') durationDelay: ElementRef

  protected readonly PlanChoiceValue = PlanChoiceValue;
  registerForm: UntypedFormGroup;
  minDate: Date;
  maxDate: Date;
  previousDelay = 0;
  selected = '-1';
  choosePlan = false;
  invalidExpiration = false;
  invalidDelay = false;
  maxCount: number;
  selectedLanguage: string;
  sendEmail = false;
  maxDrivePerInstallation = MaxDrivePerInstallation.HARDWARE;
  maxDurationDays: number;

  fTitle: string;
  fLicenses: string;
  fAvailable: string;
  fNotify: string;
  fCancel: string;
  fSave: string;
  fBusiness: string;
  subTypes = selectableTypes;
  subType = selectableTypes[selectableTypes.length - 1];

  plusCount: number;
  selectCount: number;
  hardwareCount: number;
  hardware2Count: number;
  bitLockerCount: number;
  fdeCount: number;
  agentCount: number;
  selectedEdition: PlanChoices;
  readonly defaultDate = dayjs.utc().add(1, 'year').endOf('day').toDate();
  readonly defaultFdeDate = dayjs.utc('2050-01-01T00:00:00Z').endOf('day').toDate();
  readonly defaultFdeMaintenanceDate = dayjs.utc().add(3, 'year').endOf('day').toDate();

  constructor(
    public dialogRef: MatDialogRef<AddChildSubscriptionComponent>,
    private formBuilder: UntypedFormBuilder,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: IAddOrganizationData,
  ) {
    this.translate.get(['CANCEL_CAP', 'SAVE_CAP', 'ORG_ADD_SUBSCRIPTION', 'ADO_COPY',
      'ADO_LICENSES', 'ADO_AVAILABLE', 'ADO_EXPIRES', 'ADO_DEFAULT', 'ADO_NOTIFY',
      'ADS_PERSONAL', 'ADS_PREMIUM']).subscribe(vals => {
      this.fCancel = vals['CANCEL_CAP'];
      this.fSave = vals['SAVE_CAP'];
      this.fTitle = vals['ORG_ADD_SUBSCRIPTION'];
      this.fLicenses = vals['ADO_LICENSES'];
      this.fAvailable = vals['ADO_AVAILABLE'];
      this.fNotify = vals['ADO_NOTIFY'];
      this.fBusiness = vals['ADS_PREMIUM'];
    });
    this.minDate = data.minDate;
    this.maxDate = data.maxDate;
    this.choosePlan = data.choosePlan;
    this.maxCount = this.getMaxCount(data);
    this.selectedLanguage = localStorage.getItem('language') || 'en';
    this.selectedEdition = data.edition;
    this.plusCount = data.plusCount;
    this.selectCount = data.selectCount;
    this.hardwareCount = data.hardwareCount;
    this.hardware2Count = data.hardware2Count;
    this.bitLockerCount = data.bitLockerCount;
    this.fdeCount = data.fdeCount;
    this.agentCount = data.agentCount;
    this.maxDurationDays = data.maxDurationDays;
    this.registerForm = this.formBuilder.group({
      licenses: ['', Validators.required],
      edition: [this.selectedEdition ? this.selectedEdition : PlanChoiceValue.PLUS, Validators.required],
      sendEmail: [false],
      enable_pba: false,
      enablePBAExtendedFeatures: true,
      enablePBALogErase: false,
      expirationDate: [this.defaultDate],
      maintenance_end: [this.defaultDate, Validators.required],
    });
    this.registerForm.controls['enablePBAExtendedFeatures'].setValue(data.enablePBAExtendedFeatures);
    this.registerForm.controls['enablePBALogErase'].setValue(data.enablePBALogErase);
  }

  private getMaxCount(data) {
    if ( data.edition === PlanChoiceValue.PLUS) {
      return data.plusCount;
    }
    if ( data.edition === PlanChoiceValue.HARDWARE ) {
      return data.hardwareCount;
    }
    if ( data.edition === PlanChoiceValue.HARDWARE2 ) {
      return data.hardware2Count;
    }
    if ( data.edition === PlanChoiceValue.BITLOCKER ) {
      return data.bitLockerCount;
    }
    if ( data.edition === PlanChoiceValue.FDE ) {
      return data.fdeCount;
    }
    if ( data.edition === PlanChoiceValue.AGENT ) {
      return data.agentCount;
    }
    // default to select count
    return data.selectCount;
  }

  ngOnInit(): void {
    this.subType = selectableTypes[selectableTypes.length - 1];
  }

  onSendEmailCheckChanged(event) {
    this.sendEmail = event?.checked;
  }

  onLanguageChanged(language) {
    this.selectedLanguage = language;
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  onExpirationChanged() {
    this.resetDurationValues();
  }

  resetDurationValues() {
    if (this.durationDelay && this.durationDelay.nativeElement) {
        this.durationDelay.nativeElement.value = null;
    }
    this.maxDurationDays = null;
    this.previousDelay = 0;
  }

  onSubmit() {
    const result = {
      licenses: this.registerForm.value.licenses,
      expirationDate: this.registerForm.value.expirationDate,
      maintenance_end: this.registerForm.value.maintenance_end,
      maxDurationDays: this.data.maxDurationDays,
      edition: this.registerForm.value.edition,
      subType: this.subType.value,
      sendEmail: this.sendEmail,
      language: this.selectedLanguage,
      enablePBAExtendedFeatures: this.registerForm.value.enablePBAExtendedFeatures,
      enablePBALogErase: this.registerForm.value.enablePBALogErase
    };
    this.dialogRef.close(result);
  }

  onEditionChanged(edition: PlanChoices) {

    if (edition === PlanChoiceValue.HARDWARE) {
      this.maxDrivePerInstallation = MaxDrivePerInstallation.HARDWARE;
      this.maxCount = this.hardwareCount;
      this.registerForm.controls['expirationDate'].setValue(null);
      this.registerForm.controls['maintenance_end'].setValue(this.defaultDate);
    } else if (edition === PlanChoiceValue.HARDWARE2) {
      this.maxCount = this.hardware2Count;
      this.maxDrivePerInstallation = MaxDrivePerInstallation.HARDWARE2;
      this.registerForm.controls['expirationDate'].setValue(null);
      this.registerForm.controls['maintenance_end'].setValue(this.defaultDate);
    } else if ( edition === PlanChoiceValue.BITLOCKER ) {
      this.maxCount = this.bitLockerCount;
      this.registerForm.controls['expirationDate'].setValue(this.defaultDate);
      this.registerForm.controls['maintenance_end'].setValue(this.defaultDate);
    } else if ( edition === PlanChoiceValue.FDE  ) {
      this.maxCount = this.fdeCount;
      this.registerForm.controls['expirationDate'].setValue(this.defaultFdeDate);
      this.registerForm.controls['maintenance_end'].setValue(this.defaultFdeMaintenanceDate);
    } else if ( edition === PlanChoiceValue.AGENT  ) {
      this.maxCount = this.agentCount;
      this.registerForm.controls['expirationDate'].setValue(this.defaultDate);
      this.registerForm.controls['maintenance_end'].setValue(this.defaultDate);
    } else {
      this.maxCount = this.plusCount;
      this.registerForm.controls['expirationDate'].setValue(this.defaultDate);
      this.registerForm.controls['maintenance_end'].setValue(this.defaultDate);
    }
    this.selectedEdition = edition;
    this.resetDurationValues();
  }

  onDelayChange(ev: any) {
    const delayMonths = +ev.target.value;
    const delta = delayMonths - this.previousDelay;
    this.minDate = dayjs.utc(this.data.minDate).add(delayMonths, 'months').toDate();
    this.maxDate = dayjs.utc(this.data.maxDate).add(delayMonths, 'months').toDate();
    this.registerForm.controls['expirationDate'].setValue(
      dayjs.utc(this.registerForm.value['expirationDate']).add(delta, 'months').toDate());
    this.registerForm.controls['maintenance_end'].setValue(
      dayjs.utc(this.registerForm.value['maintenance_end']).add(delta, 'months').toDate());
    this.previousDelay = delayMonths;

    const today = dayjs();
    const futureDay = dayjs(this.registerForm.controls.expirationDate.value);
    this.maxDurationDays = futureDay.diff(today, 'day');
  }

}
